/**
 * A guide entry read from an event channel's own name (playbook Phase 3f, "guide
 * from channel names"). Providers carry streaming events in numbered slots and
 * write the event into the slot's name, for example (the owner's provider, onn
 * box 2026-10-23):
 *
 *   "NCAAW Soccer: SMU vs California @ Oct 22 3:00 PM :SEC+ 03"
 *   "The Pat McAfee Show: The Pat McAfee Show @ Oct 22 12:00 PM :ESPN+ 03"
 *
 * The name gives the title and the start, not the end, and no zone. The times
 * are US Eastern (checked against shows with known times: The Pat McAfee Show
 * and The Rich Eisen Show both start at noon Eastern), unless the name says
 * otherwise. An event is taken to run EVENT_HOURS. A slot named by network and
 * number only (":SEC+ 06", "PPV 01:") has nothing to read and gets nothing.
 */
/** How long an event read from a name is shown for: the name gives no end. */
export const EVENT_HOURS = 3;
const MONTHS = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11 };
const ZONES = {
    et: "America/New_York",
    est: "America/New_York",
    edt: "America/New_York",
    ct: "America/Chicago",
    cst: "America/Chicago",
    cdt: "America/Chicago",
    mt: "America/Denver",
    mst: "America/Denver",
    mdt: "America/Denver",
    pt: "America/Los_Angeles",
    pst: "America/Los_Angeles",
    pdt: "America/Los_Angeles",
    utc: "UTC",
    gmt: "UTC",
};
/** "Title @ [Thu] Oct 22[, 2026] 3[:00] PM [ET]", anything after it (the slot) ignored. */
const EVENT_NAME = /^(.*?\S)\s*@\s*(?:[a-z]{3},?\s+)?([a-z]{3})[a-z]*\.?\s+(\d{1,2})(?:,?\s+(\d{4}))?,?\s+(\d{1,2})(?::(\d{2}))?\s*([ap])\.?m\.?(?:\s+([a-z]{2,3})\b)?/i;
/** Milliseconds a zone is ahead of UTC at a moment (negative for the Americas). */
function zoneOffsetMs(utcMs, timeZone) {
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone,
        hourCycle: "h23",
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        second: "numeric",
    }).formatToParts(new Date(utcMs));
    const value = (type) => Number(parts.find((part) => part.type === type)?.value ?? 0);
    const asUtc = Date.UTC(value("year"), value("month") - 1, value("day"), value("hour"), value("minute"), value("second"));
    return asUtc - Math.floor(utcMs / 1000) * 1000;
}
/** A wall-clock time in a zone, as a moment. */
function zonedTime(year, month, day, hour, minute, timeZone) {
    const wall = Date.UTC(year, month, day, hour, minute);
    const first = wall - zoneOffsetMs(wall, timeZone);
    // Once more, for a time near a daylight-saving change.
    return wall - zoneOffsetMs(first, timeZone);
}
/** "The Golics: The Golics" → "The Golics": a show named twice says it once. */
function tidyTitle(title) {
    const repeated = /^(.+?):\s*(.+)$/.exec(title);
    if (repeated && repeated[1].trim().toLowerCase() === repeated[2].trim().toLowerCase())
        return repeated[1].trim();
    return title.trim();
}
/**
 * The event a channel's name announces, or null. The year is the one that puts
 * the date nearest `nowMs` (names carry none), so a December name read in
 * January is last year's.
 */
export function eventFromChannelName(name, nowMs) {
    const found = EVENT_NAME.exec(name);
    if (!found)
        return null;
    const [, rawTitle, monthName, dayText, yearText, hourText, minuteText, half, zoneText] = found;
    const month = MONTHS[monthName.toLowerCase()];
    const day = Number(dayText);
    let hour = Number(hourText);
    const minute = minuteText ? Number(minuteText) : 0;
    if (month === undefined || day < 1 || day > 31 || hour > 23 || minute > 59)
        return null;
    // "19:00 PM" (a MAX slot, onn box 2026-10-23) is a 24-hour time with the half added.
    if (hour > 12 && half.toLowerCase() === "a")
        return null;
    if (hour >= 1 && hour <= 12) {
        if (half.toLowerCase() === "p" && hour !== 12)
            hour += 12;
        if (half.toLowerCase() === "a" && hour === 12)
            hour = 0;
    }
    const timeZone = (zoneText && ZONES[zoneText.toLowerCase()]) || ZONES.et;
    const thisYear = new Date(nowMs).getUTCFullYear();
    const years = yearText ? [Number(yearText)] : [thisYear - 1, thisYear, thisYear + 1];
    let startMs = Number.NaN;
    for (const year of years) {
        const at = zonedTime(year, month, day, hour, minute, timeZone);
        if (Number.isNaN(startMs) || Math.abs(at - nowMs) < Math.abs(startMs - nowMs))
            startMs = at;
    }
    const title = tidyTitle(rawTitle);
    if (!title || Number.isNaN(startMs))
        return null;
    return { title, startMs, endMs: startMs + EVENT_HOURS * 3_600_000 };
}
//# sourceMappingURL=eventNames.js.map